import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSession } from '@baltlife/internal-sso-react';
import { decodeTokenClaims, parseAppRoles } from '@baltlife/internal-sso-core';
import { devAuthBypass } from '../auth/sso';

const UserContext = createContext();

export function useUser() {
  return useContext(UserContext);
}

// How long to wait for the access token to reach useSession before assuming it never
// will. Token hydration after the route guard is sub-second; this only trips on a real
// failure (token endpoint down, refresh rejected). Without it an unresolved session
// renders null forever — a silent blank page, which reports as "the site is broken".
const SESSION_RESOLVE_TIMEOUT_MS = 10000;

// Mounted inside the auth guard on the pathless layout route in App.jsx, so it wraps the
// authenticated routes only. The routes that run before a session exists — /oauth/callback,
// /Error/* — are siblings of that layout route and never mount this provider, so the
// null-gate below needs no path-shaped escape hatch.
export function UserProvider({ children }) {
  const [resolveTimedOut, setResolveTimedOut] = useState(false);
  const [user, setUser] = useState();
  const { accessToken } = useSession();

  // The guard proves a SESSION exists; it does NOT prove the access token has reached
  // useSession yet. Until it has, an absent user means "not resolved yet" — never
  // "denied". Under the dev bypass there is never a token, so dev counts as resolved or
  // this would wait out the timeout on every load.
  const sessionResolved = Boolean(accessToken) || devAuthBypass;

  useEffect(() => {
    // Identity is decoded from the internal-SSO access token (claim template
    // preserves the extension_* names AgentPortal reads). decodeTokenClaims returns the
    // whole JWT payload, so every extension_* field resolves.
    const claims = accessToken ? decodeTokenClaims(accessToken) : null;
    setUser(new User({ userInfo: claims || undefined }));
  }, [accessToken]);

  // Bound the unresolved window so a token that never arrives surfaces an error the user
  // can act on instead of an indefinite blank page.
  useEffect(() => {
    if (sessionResolved) {
      setResolveTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setResolveTimedOut(true), SESSION_RESOLVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [sessionResolved]);

  if (!sessionResolved) {
    if (!resolveTimedOut) return null;
    return (
      <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>We couldn&apos;t finish signing you in.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  // Resolved: from here a failing check is a real eligibility denial, not a pending token.
  if (
    !user ||
    !user.npnID ||
    user.npnID == '' ||
    (user.npnID === 'HO' && !user.isHO) ||
    (user.npnID === '0' && !user.isHO)
  ) {
    return null;
  }
  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

export class User {
  constructor({ userInfo }) {
    this.division = userInfo?.extension_division;
    // Optional-chain both hops: a token missing extension_producerid must
    // degrade gracefully, not crash the provider.
    this.producerID = userInfo?.extension_producerid?.split(',')[0];
    this.LOB = User.parseLob(userInfo?.extension_lob);
    this.permissions = this.getPermissions(userInfo);
    this.agency = userInfo?.extension_agency;
    this.agencyID = userInfo?.extension_agencyid;
    this.emailAddress = userInfo?.email;
    this.npnID = userInfo?.extension_npnid;
    // Order matters: this invokes the prototype method and then SHADOWS it with the
    // boolean. The method reads producerID and npnID, so both must already be assigned
    // above — move this line up and every home-office user silently becomes non-HO.
    this.isHO = this.isHO();
    this.hasMultipleProducerIDs = userInfo?.extension_producerid?.split(',').length > 1;
    this.userType = userInfo?.extension_usertype;
    this.displayName = userInfo?.name;
    this.producerIDs = userInfo?.extension_producerid;
    // Stable owner key for audit/lock ownership (display name is a label only).
    this.blcUserId = userInfo?.extension_blcuserid;
  }
  // extension_lob is a JSON-encoded array. Guard the parse: a malformed, empty or absent
  // claim must yield [] rather than throw out of the constructor, which would take the
  // whole provider — and therefore the whole app — down with it.
  static parseLob(raw) {
    if (raw === undefined || raw === null || raw === '') return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }
  // Two conditions on purpose, and stricter than the other consumer apps: a producerID of
  // HO alone does not make a home-office user here.
  isHO() {
    return this?.producerID === 'HO' && this?.npnID === 'HO' ? true : false;
  }
  // parseAppRoles takes the whole claims object, reads extension_approles itself, tolerates
  // the CSV and array shapes, drops empties and strips exactly one leading `ACL-`. It
  // returns a string ARRAY — but permissions is consumed as an object MAP: spread as props
  // (<AdminMenuItem {...user.permissions} />) and counted (Object.keys(...).length) as the
  // signal for "has any access at all". Fold it back to {Role: true} or the nav empties and
  // every user is bounced to Access Denied.
  //
  // This also fixes multi-token roles. The previous split('-')[1] turned ACL-InSpeed-Admin
  // into `InSpeed`; the role is `InSpeed-Admin`.
  //
  // The claim template may emit extension_approles (canonical) or approles; accept either
  // so a template naming mismatch does not read as "no permissions".
  getPermissions(userInfo) {
    const permissions = {};
    const roles = parseAppRoles({
      extension_approles: userInfo?.extension_approles ?? userInfo?.approles,
    });
    for (const role of roles) {
      permissions[role] = true;
    }
    return permissions;
  }
  hasProducerID(producerID) {
    if (!this.producerIDs || !producerID || producerID == '') return false;

    //check if the producerID param exists in user claims
    if (this.producerIDs !== undefined && this.producerIDs !== null)
      return this.producerIDs.split(',').includes(producerID);

    return false;
  }
}
